/**
 * Personalized distribution's notifications: one place for the lifecycle wording (prototype
 * 60-notify `DIST` presets, plus "finished with problems"). Each preset is pure and returns a
 * `NotificationInput` for `notify()` (services/notify). Names, counts and times come from the
 * caller's data; nothing here is a sample value.
 *
 *   notify(distributionNotices.activated({ name, copies, people, first, path }));
 *
 * Every notification about one run shares `distributionRunNoticeId(runId)`, so its progress and
 * its result update the same toast in place.
 */
import type { NotificationInput } from "../notify";
import { DISTRIBUTION_RUN_TIME_COPY as TIME_COPY } from "../../constants/distributionRunsCopy";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import { pluralize } from "./distributionFormat";


const quoted = (name: string): string => `“${name}”`;

/** The id every notification about one run shares. */
export const distributionRunNoticeId = (runId: string): string => `distribution-run-${runId}`;

/** "Started 2 minutes ago": the run's start through the app's relative-time util; null with no time. */
export const startedLine = (startedAt?: string | null): string | null => (startedAt ? TIME_COPY.started(formatRelativeTime(startedAt)) : null);

/** "Queued 2 minutes ago": for a run that has not started yet; null with no time. */
export const queuedLine = (queuedAt?: string | null): string | null => (queuedAt ? TIME_COPY.queued(formatRelativeTime(queuedAt)) : null);

/** The run's start line, or, before it has started, when it was queued. */
const beginLine = ({ startedAt, queuedAt }: Pick<RunNoticeContext, "startedAt" | "queuedAt">): string | null =>
  startedLine(startedAt) ?? queuedLine(queuedAt);

/** "Finished 2 minutes ago", or null with no time. */
export const finishedLine = (finishedAt?: string | null): string | null => (finishedAt ? TIME_COPY.finished(formatRelativeTime(finishedAt)) : null);

/** Today's text with the time line after it ("Finished 2 minutes ago."), or before it when `lead`; unchanged with none. */
const withTime = (text: string, line: string | null, lead = false): string => (line ? (lead ? `${line}. ${text}` : `${text} ${line}.`) : text);

const viewRun = (path: string | undefined) => (path ? { label: "View run", to: path } : null);

/** Where a live run is: checking the data, making copies, or sending them. */
export type DistributionRunPhase = "check" | "make" | "send";

export interface RunNoticeContext {
  runId: string;
  /** The distribution's name. */
  name: string;
  /** The run page, for "View run". */
  path?: string;
  /** ISO time the run started; "Started 2 min ago" when given. */
  startedAt?: string | null;
  /** ISO time the run was queued; "Queued 2 min ago" when it has not started yet. */
  queuedAt?: string | null;
  /** ISO time the run finished; "Finished 2 min ago" when given. */
  finishedAt?: string | null;
}

export interface RunProgressContext extends RunNoticeContext {
  phase: DistributionRunPhase;
  /** Copies made (make) or deliveries done (send). */
  done: number;
  /** Copies to make (make) or deliveries to send (send). */
  total: number;
}

export interface RunResultContext extends RunNoticeContext {
  /** Deliveries that went out. */
  sent: number;
  /** Every delivery of the run. */
  total: number;
  copies: number;
  failed: number;
  /** Held until someone fixes the row. */
  held: number;
  /** Sent, with a note (e.g. one format was left out). */
  warned: number;
  /** "Tue, Oct 27 at 8:00 AM PT", when it runs again. */
  next?: string | null;
}

/** "2 didn’t arrive, 1 held, 1 warning" — what went wrong in a run, in order of weight. */
export const runProblemBits = ({ failed, held, warned }: Pick<RunResultContext, "failed" | "held" | "warned">): string[] =>
  [
    failed ? `${failed} didn’t arrive` : null,
    held ? `${held} held` : null,
    warned ? pluralize(warned, "warning", "warnings") : null,
  ].filter((bit): bit is string => Boolean(bit));

const capitalised = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** The phase's share of the bar: making copies fills 80%, sending the rest (prototype `runProgress`). */
export const runProgressShare = (phase: DistributionRunPhase, done: number, total: number): number => {
  const share = total > 0 ? Math.min(1, Math.max(0, done / total)) : 0;
  if (phase === "check") return 0.04;
  if (phase === "make") return 0.08 + 0.72 * share;
  return 0.8 + 0.2 * share;
};

export const runProgressTitle = (phase: DistributionRunPhase, done: number, total: number, name = ""): string => {
  const of = (verb: string, count: number) => `${name ? `${verb} ${name} · ` : `${verb} `}${count} of ${total}`;
  if (phase === "check") return name ? `Checking the data for ${name}` : "Checking the data";
  if (phase === "make") return of("Making copies for", Math.min(done, total));
  if (total <= 0) return name ? `Sending ${name}` : "Sending";
  return of("Sending", Math.min(done + 1, total));
};

const KEEPS_RUNNING = "It keeps running if you leave this page.";

/** What a title leads with when the distribution has no name yet. */
const NAMELESS = "Distribution";

/** "{name} · {fact}": the notification title leads with the distribution. */
const named = (name: string, fact: string): string => `${name.trim() || NAMELESS} · ${fact}`;

export const distributionNotices = {
  draftSaved: ({ name, path }: { name: string; path?: string }): NotificationInput => ({
    kind: "success",
    title: "Draft saved",
    body: `${quoted(name)} is in your distributions list. Resume it any time.`,
    action: path ? { label: "Open the list", to: path } : null,
  }),

  changesSaved: ({ name }: { name: string }): NotificationInput => ({
    kind: "success",
    title: "Changes saved",
    body: `${quoted(name)} uses them from the next run.`,
  }),

  imported: ({ rows, toFix }: { rows: number; toFix: number }): NotificationInput =>
    toFix > 0
      ? {
          kind: "warning",
          title: `${pluralize(rows, "recipient", "recipients")} imported, ${toFix} to fix`,
          body: `Fix ${toFix === 1 ? "it" : "them"} before you activate. The other ${rows - toFix} ${rows - toFix === 1 ? "is" : "are"} ready.`,
        }
      : { kind: "success", title: `${pluralize(rows, "recipient", "recipients")} imported`, body: "Every row is ready." },

  activated: ({ name, copies, people, first, path }: { name: string; copies: number; people: number; first?: string | null; path?: string }): NotificationInput => ({
    kind: "success",
    title: `${name} is active`,
    body: first ? `First send ${first}` : `Sends ${pluralize(copies, "copy", "copies")} to ${pluralize(people, "person", "people")}`,
    action: path ? { label: "View", to: path } : null,
  }),

  /** `sentTo`: "you", an address, or "3 addresses". */
  testSent: ({ sentTo = "you", person, value, path }: { sentTo?: string; person?: string | null; value?: string | null; path?: string }): NotificationInput => ({
    kind: "success",
    icon: "send",
    title: `Test sent to ${sentTo}`,
    body: person && value ? `${person}’s ${value} copy arrives in a few minutes.` : "The copy arrives in a few minutes.",
    action: viewRun(path),
  }),

  runStarted: ({ runId, name, path, startedAt, queuedAt }: RunNoticeContext): NotificationInput => ({
    id: distributionRunNoticeId(runId),
    kind: "progress",
    title: named(name, "started"),
    body: withTime(KEEPS_RUNNING, beginLine({ startedAt, queuedAt }), true),
    progress: 0,
    action: viewRun(path),
  }),

  scheduleFired: ({ runId, name, at, path, startedAt, queuedAt }: RunNoticeContext & { at?: string | null }): NotificationInput => ({
    id: distributionRunNoticeId(runId),
    kind: "progress",
    title: named(name, "scheduled run started"),
    body: at ? `Started at ${at}. ${KEEPS_RUNNING}` : withTime(KEEPS_RUNNING, beginLine({ startedAt, queuedAt }), true),
    progress: 0,
    action: viewRun(path),
  }),

  runProgress: ({ runId, name, phase, done, total, path, startedAt, queuedAt }: RunProgressContext): NotificationInput => ({
    id: distributionRunNoticeId(runId),
    kind: "progress",
    title: runProgressTitle(phase, done, total, name),
    body: withTime(KEEPS_RUNNING, beginLine({ startedAt, queuedAt }), true),
    progress: runProgressShare(phase, done, total),
    action: viewRun(path),
  }),

  runCompleted: ({ runId, name, sent, copies, next, path, finishedAt }: Pick<RunResultContext, "runId" | "name" | "sent" | "copies" | "next" | "path" | "finishedAt">): NotificationInput => ({
    id: distributionRunNoticeId(runId),
    kind: "success",
    title: named(name, `delivered to ${sent}`),
    body: withTime(`${capitalised(pluralize(copies, "copy", "copies"))} sent to ${pluralize(sent, "person", "people")}.${next ? ` Next run ${next}.` : ""}`, finishedLine(finishedAt)),
    action: viewRun(path),
  }),

  /** Some got their copy, some didn't (or only warnings): error when anyone missed out. */
  runFinishedWithProblems: (context: RunResultContext): NotificationInput => {
    const missedOut = context.failed > 0 || context.held > 0;
    const bits = runProblemBits(context);
    return {
      id: distributionRunNoticeId(context.runId),
      kind: missedOut ? "error" : "warning",
      title: named(context.name, bits.join(", ")),
      body: withTime(`${context.sent} of ${context.total} got their copy. Open the run to see why and how to fix it.`, finishedLine(context.finishedAt)),
      action: viewRun(context.path),
    };
  },

  /** Nothing went out. `reason` is the backend's message, verbatim. */
  runFailed: ({ runId, name, reason, path, finishedAt }: RunNoticeContext & { reason?: string | null }): NotificationInput => ({
    id: distributionRunNoticeId(runId),
    kind: "error",
    title: named(name, "nothing was sent"),
    body: withTime(reason ? reason : "Open the run to see why.", finishedLine(finishedAt)),
    action: viewRun(path),
  }),

  /** The run page can't be read for a while; the run itself goes on. `message` is the backend's. */
  lostTrack: ({ runId, name, message, path }: RunNoticeContext & { message: string }): NotificationInput => ({
    id: distributionRunNoticeId(runId),
    kind: "info",
    title: named(name, "couldn’t follow the run"),
    body: `${message} The run keeps going. Open it to see how it went.`,
    action: viewRun(path),
  }),

  alreadyRunning: ({ name, path }: { name: string; path?: string }): NotificationInput => ({
    kind: "info",
    title: `${quoted(name)} is already running`,
    body: "Wait for it to finish before you send it again.",
    action: viewRun(path),
  }),

  paused: ({ name, onResume }: { name: string; onResume?: () => void }): NotificationInput => ({
    kind: "info",
    icon: "pause",
    title: "Distribution paused",
    body: `${quoted(name)} sends nothing until you resume it.`,
    action: onResume ? { label: "Resume", onClick: onResume } : null,
  }),

  resumed: ({ name, next }: { name: string; next?: string | null }): NotificationInput => ({
    kind: "success",
    icon: "play",
    title: "Distribution resumed",
    body: next ? `${quoted(name)} sends next on ${next}.` : `${quoted(name)} sends on its schedule again.`,
  }),

  deleted: ({ name }: { name: string }): NotificationInput => ({
    kind: "success",
    title: "Distribution deleted",
    body: `${quoted(name)} won’t send again.`,
  }),

  archived: ({ name }: { name: string }): NotificationInput => ({
    kind: "success",
    title: "Distribution archived",
    body: `${quoted(name)} won’t send again.`,
  }),

  restored: ({ name, status }: { name: string; status: "paused" | "draft" }): NotificationInput => ({
    kind: "success",
    title: "Distribution restored",
    body:
      status === "paused"
        ? `${quoted(name)} is back, paused. Resume it when you want it to send again.`
        : `${quoted(name)} is back as a draft. Finish setting it up to send.`,
  }),

  /** A write the backend refused: its message, verbatim, under a short title. */
  failed: ({ title, message }: { title: string; message: string }): NotificationInput => ({
    kind: "error",
    title,
    body: message,
  }),
};
